import React, { useState, useEffect, useCallback } from 'react';
import { Container, Button, Table, Spinner, Alert, Row, Col, Card, Form } from 'react-bootstrap';
import apiClient, { API_ENDPOINTS } from '../utils/api';
import { formatCurrency } from '../utils/helpers';

const thisYear = new Date().getFullYear();

// 決算資料（運営内部用）。オーナーに共有する精算書とは別物で、
// 売上・経費を勘定科目別に集計し、税理士に渡せる仕訳CSVを出力する。
function Accounting() {
  const [year, setYear] = useState(thisYear);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await apiClient.get(API_ENDPOINTS.LEDGER_TAX_SUMMARY, { params: { year } });
      setSummary(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || '決算集計の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const downloadCsv = async (url, filename, doneMessage) => {
    setError('');
    try {
      const res = await apiClient.get(url, { responseType: 'blob' });
      const objectUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
      setMessage(doneMessage);
    } catch (err) {
      setError('CSVの出力に失敗しました');
    }
  };

  if (loading && !summary) {
    return (
      <Container className="d-flex justify-content-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  const activeMonths = (summary?.months || []).filter((m) => m.active);

  return (
    <Container fluid>
      <div className="page-header">
        <h1 className="page-title">決算資料</h1>
      </div>

      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <p className="text-muted small mb-3">
        運営内部用の資料です（オーナーへの共有は不要）。{summary?.fiscal_note}で、
        オーナー精算と同じ台帳データを勘定科目別に集計しています。
        OTA手数料→支払手数料、清掃費→外注費、備品費・固定費→登録時の科目に自動分類。
      </p>

      <div className="toolbar-row mb-3 d-flex flex-wrap gap-2 align-items-center">
        <Form.Select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{ maxWidth: '140px' }}
        >
          {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map((y) => (
            <option key={y} value={y}>{y}年</option>
          ))}
        </Form.Select>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => downloadCsv(
            `${API_ENDPOINTS.LEDGER_EXPORT_TAX}/${year}`,
            `決算用科目別集計_${year}.csv`,
            `${year}年の科目別集計CSVを出力しました`,
          )}
        >
          科目別集計CSV
        </Button>
        <Button
          variant="outline-success"
          size="sm"
          onClick={() => downloadCsv(
            `${API_ENDPOINTS.LEDGER_EXPORT_JOURNAL}/${year}`,
            `仕訳一覧_${year}.csv`,
            `${year}年の仕訳CSVを出力しました（税理士渡し用の下書き）`,
          )}
          title="日付・借方・貸方・金額・摘要の汎用仕訳CSV。相手科目は仮置きです"
        >
          仕訳CSV（税理士渡し用）
        </Button>
        {loading && <Spinner animation="border" size="sm" />}
      </div>

      {summary && (
        <>
          <Row className="mb-3 g-2">
            {[
              ['売上高', summary.sales_total, ''],
              ['費用合計（オーナー分配含む）', -summary.expenses_grand_total, 'text-danger'],
              ['利益（分配前）', summary.profit_before_split, ''],
              ['オーナー分配', -summary.owner_share_total, 'text-danger'],
              ['運営利益（分配後）', summary.operator_profit, summary.operator_profit < 0 ? 'text-danger' : 'text-success'],
            ].map(([label, value, cls]) => (
              <Col key={label} xs={6} md={4} lg={2}>
                <Card className="h-100">
                  <Card.Body className="py-2 px-3">
                    <div className="text-muted small">{label}</div>
                    <div className={`fw-bold ${cls}`}>{formatCurrency(value)}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <h6>科目別集計（{year}年）</h6>
          <Table striped bordered hover responsive size="sm" className="data-table mb-4" style={{ maxWidth: '900px' }}>
            <thead>
              <tr>
                <th>区分</th>
                <th>勘定科目</th>
                <th>金額</th>
                <th>内訳</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>収益</td>
                <td>売上高</td>
                <td className="fw-bold">{formatCurrency(summary.sales_total)}</td>
                <td className="text-muted small">宿泊売上 {summary.booking_count}件（チェックイン月基準・キャンセル除く）</td>
              </tr>
              {summary.expense_lines.map((l) => (
                <tr key={l.account}>
                  <td>費用</td>
                  <td>{l.account}</td>
                  <td className="text-danger">{formatCurrency(-l.amount)}</td>
                  <td className="text-muted small">{l.detail}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="fw-bold">
                <td colSpan={2}>運営利益（オーナー分配後）</td>
                <td colSpan={2}>{formatCurrency(summary.operator_profit)}</td>
              </tr>
            </tfoot>
          </Table>

          <h6>月別内訳</h6>
          <Table striped bordered hover responsive size="sm" className="data-table mb-4" style={{ maxWidth: '900px' }}>
            <thead>
              <tr>
                <th>月</th>
                <th>予約数</th>
                <th>売上</th>
                <th>経費計</th>
                <th>利益</th>
                <th>オーナー分配</th>
                <th>運営取り分</th>
              </tr>
            </thead>
            <tbody>
              {activeMonths.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted">{year}年の実績はまだありません</td></tr>
              ) : (
                activeMonths.map((m) => (
                  <tr key={m.month}>
                    <td>{m.month}</td>
                    <td>{m.booking_count}</td>
                    <td>{formatCurrency(m.gross)}</td>
                    <td className="text-danger">{formatCurrency(-m.costs)}</td>
                    <td className={m.profit < 0 ? 'text-danger' : ''}>{formatCurrency(m.profit)}</td>
                    <td>{formatCurrency(m.owner_share)}</td>
                    <td>{formatCurrency(m.operator_share)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Alert variant="light" className="border small text-muted">
            仕訳CSVの注意: 貸方の相手科目（売掛金・未払金・現金・普通預金）は入出金方法が台帳に無いため仮置きです。
            「オーナー分配金」の正式な勘定科目（賃借料・外注費など）は契約実態に応じて税理士にご確認ください。
            消費税区分は含まれません。
          </Alert>
        </>
      )}
    </Container>
  );
}

export default Accounting;
